// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import {
    ADDRESS_NUDGE_TOOLTIP_STATUS,
    AddressInfo,
    ReviewedCartApiResponse,
    UserAddress,
} from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

// contsxts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { isAwayLocationWithoutOFO } from "@cart-client/helpers/Cart";
import { getAllDeliveryValidAddresses, getSelectedAddress } from "@cart-client/helpers/CartFooter";
import { openEditNumberBottomSheet } from "@cart-client/helpers/OFO";

// constants
import { DELIVERY_ADDRESS_ID, SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";

// hooks
import useFetchAddressList from "./useFetchAddressList";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { useCartAddressNudgeAnalytics } from "./useCartAddressNudgeAnalytics";

interface useFetchAddressOnClickReturnType {
    goToAddAddress: ({
        selectedAddress,
        addressList,
        source,
    }: {
        selectedAddress?: AddressInfo | null;
        addressList?: AddressInfo[];
        source?: string;
    }) => void;
    onSelectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => Promise<void>;
    goToSelectAddress: () => void;
    onAddressListClose: () => void;
    selectedAddress: UserAddress | null;
    displayAddressList: AddressInfo[] | UserAddress[];
}

export const useFetchAddressOnClick = (
    cartTotal: number | undefined,
    addresses: ReviewedCartApiResponse["addresses"] | undefined,
    isCartStatusSuccess?: boolean,
): useFetchAddressOnClickReturnType => {
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { localCart } = cartState;
    const { cartAddressState, setCartAddressState } = useContext(CartAddressContext);
    const { addressList } = cartAddressState;

    const { isCartAddressSelectorDisabled, fetchAddressList, addNewAddress } = useFetchAddressList();
    const {
        onClickSelectAddress,
        onClickChangeAddress,
        onClickAddressChangeBack,
        onClickAddNewAddress,
    } = useCartAddressNudgeAnalytics();

    const openAddReceiverDetailsSheet = useCallback(
        (newAddress: UserAddress) => {
            openEditNumberBottomSheet(newAddress, SOCIAL_OFO_TEXT.changeAction).then(() => {
                doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: newAddress.id,
                });
            });
        },
        [doUpdateCart, localCart],
    );

    const hideNudgeAndCloseBottomSheet = useCallback(() => {
        setCartAddressState(prevCartAddressState => ({
            ...prevCartAddressState,
            showAddressList: false,
            addressNudgeConfig: {
                ...prevCartAddressState.addressNudgeConfig,
                showAddressNudgeIcon: false,
                addressNudgeTooltipStatus:
                    prevCartAddressState.addressNudgeConfig.addressNudgeTooltipStatus ===
                    ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW
                        ? ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN
                        : prevCartAddressState.addressNudgeConfig.addressNudgeTooltipStatus,
            },
        }));
    }, [setCartAddressState]);

    const onSelectAddress = useCallback(
        async (selectedAddress: AddressInfo, addressList: AddressInfo[]) => {
            onClickSelectAddress(selectedAddress, addressList);

            // on address change hide the bottom sheet, if addressNudgeTootlip is showing close it and remove addressNudgeIcon
            hideNudgeAndCloseBottomSheet();

            try {
                document.cookie = `${DELIVERY_ADDRESS_ID}=${selectedAddress.id}`;
            } catch (error) {
                console.error("Failed to set delivery address cookie:", error);
            }

            if (!updateCartInProgress) {
                const data = await doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: selectedAddress.id,
                });
                if (data && isAwayLocationWithoutOFO(data)) {
                    let newSelectedAddress: UserAddress | null = null;
                    data?.data?.addresses?.filter((address: UserAddress) => {
                        if (address.selected) {
                            newSelectedAddress = address;
                        }
                    });
                    newSelectedAddress && openAddReceiverDetailsSheet(newSelectedAddress);
                }
            }
        },
        [
            onClickSelectAddress,
            hideNudgeAndCloseBottomSheet,
            updateCartInProgress,
            doUpdateCart,
            localCart,
            openAddReceiverDetailsSheet,
        ],
    );

    const selectedAddress = useMemo(() => getSelectedAddress(addresses), [addresses]);

    const goToSelectAddress = useCallback(() => {
        onClickChangeAddress(isCartStatusSuccess, cartTotal);

        // if XP is off or address list is available
        if (!isCartAddressSelectorDisabled || !isEmpty(addressList)) {
            setCartAddressState(prevCartAddressState => ({
                ...prevCartAddressState,
                showAddressList: true,
            }));
            return;
        }
        fetchAddressList();
    }, [
        onClickChangeAddress,
        cartTotal,
        isCartStatusSuccess,
        isCartAddressSelectorDisabled,
        addressList,
        fetchAddressList,
        setCartAddressState,
    ]);

    const onAddressListClose = useCallback(() => {
        onClickAddressChangeBack();
        setCartAddressState({
            ...cartAddressState,
            showAddressList: false,
        });
    }, [cartAddressState, onClickAddressChangeBack, setCartAddressState]);

    const displayAddressList = useMemo(
        () => (isCartAddressSelectorDisabled ? addressList : getAllDeliveryValidAddresses(addresses)),
        [addresses, addressList, isCartAddressSelectorDisabled],
    );

    const goToAddAddress = useCallback(
        ({
            selectedAddress,
            addressList,
            source,
        }: {
            selectedAddress?: AddressInfo | null;
            addressList?: AddressInfo[];
            source?: string;
        } = {}) => {
            onClickAddNewAddress(cartTotal, selectedAddress, addressList, source);
            addNewAddress();
        },
        [onClickAddNewAddress, cartTotal, addNewAddress],
    );

    return {
        goToAddAddress,
        onSelectAddress,
        goToSelectAddress,
        onAddressListClose,
        displayAddressList,
        selectedAddress,
    };
};
